import { addDaysSafe, toISODate } from "../../../utils/dateUtils.ts";

// HELPERS
const timeStringToMinutes = (time: string | null | undefined) => {
	if (!time) return null;

	const [h, m] = time.split(":").map(Number);

	if (Number.isNaN(h) || Number.isNaN(m)) return null;

	return h * 60 + m;
};

const getRealDate = (real: any) => {
	if (real?.date_of_service) return real.date_of_service;
	if (real?.start) return String(real.start).slice(0, 10);
	return null;
};

const getRealStartTime = (real: any) => {
	if (real?.start_time) return real.start_time;
	if (real?.startingTime) return real.startingTime;
	if (real?.start) return String(real.start).slice(11, 16);
	return null;
};

export const expandRegularSessions = ({
	regularSessions,
	realLists = [],
	rangeStart,
	rangeEnd,
	getWeekLetter,
}: {
	regularSessions: any[];
	realLists?: any[];
	rangeStart: Date;
	rangeEnd: Date;
	getWeekLetter: (date: Date) => "A" | "B" | "C" | "D";
}) => {
	const safeRealLists = Array.isArray(realLists) ? realLists : [];
	const results: any[] = [];
	const start = new Date(rangeStart);
	const end = new Date(rangeEnd);

	for (let d = new Date(start); d < end; d = addDaysSafe(d, 1)) {
		const dateStr = toISODate(d);

		const weekday = d.toLocaleDateString("en-AU", {
			weekday: "long",
		});

		const weekLetter = getWeekLetter(d);

		regularSessions.forEach((rs) => {
			if (rs.week_day !== weekday) return;
			if (rs.week_cycle !== weekLetter) return;
			if (rs.start_date && rs.start_date > dateStr) return;
			if (rs.end_date && rs.end_date < dateStr) return;

			const hasMatchingRealSession = safeRealLists.some((real) => {
				if (!real || real.isRegularSession) return false;

				const realDate = getRealDate(real);
				if (!realDate || realDate !== dateStr) return false;

				const realSurgeonId =
					real?.surgeon?.id ?? real?.surgeon_id ?? null;
				const regularSurgeonId =
					rs?.surgeon?.id ?? rs.surgeon_id ?? null;

				if (
					realSurgeonId == null ||
					regularSurgeonId == null ||
					String(realSurgeonId) !== String(regularSurgeonId)
				)
					return false;

				const realHospitalId =
					real?.hospital?.id ?? real?.hospital_id ?? null;
				const regularHospitalId =
					rs?.hospital?.id ?? rs.hospital_id ?? null;

				if (
					realHospitalId == null ||
					regularHospitalId == null ||
					String(realHospitalId) !== String(regularHospitalId)
				)
					return false;

				const realStartTime = getRealStartTime(real);
				const realMinutes = timeStringToMinutes(realStartTime);
				const regularMinutes = timeStringToMinutes(rs?.start_time);

				if (realMinutes === null || regularMinutes === null)
					return false;

				return Math.abs(realMinutes - regularMinutes) <= 120;
			});

			if (hasMatchingRealSession) return;

			results.push({
				id: `regular-${rs.id}-${dateStr}`,
				date_of_service: dateStr,
				start_time: rs.start_time,
				end_time: rs.end_time,
				surgeon: {
					id: rs?.surgeon?.id ?? rs.surgeon_id,
					acronym: rs?.surgeon?.acronym ?? "",
					first_name: rs?.surgeon?.first_name ?? "Regular",
					last_name:
						rs?.surgeon?.last_name ??
						`Surgeon ${rs.surgeon_id ?? ""}`,
					specialty: rs?.surgeon?.specialty ?? [],
				},
				hospital: {
					id: rs?.hospital?.id ?? rs.hospital_id,
					acronym: rs?.hospital?.acronym ?? "",
					name:
						rs?.hospital?.name ||
						rs?.hospital?.hospital_name ||
						`Hospital ${rs.hospital_id ?? ""}`,
				},
				events: [],
				isRegularSession: true,
				regularSessionId: rs.id,
				week_cycle: rs.week_cycle,
				week_day: rs.week_day,
				am: Boolean(rs.am),
				pm: Boolean(rs.pm),
				tw: Boolean(rs.tw),
			});
		});
	}

	return results;
};
